import { useState, useEffect } from 'react';
import { ozmapService } from '../services/ozmapService';

// `clientsStatus` acompanha a contagem de clientes separadamente do resto:
// ela depende de uma segunda rodada de requisições e não deve segurar o mapa.
// 'idle' | 'loading' | 'ready' | 'error'. Com 'ready', 0 é um valor legítimo.
const EMPTY_DATA = { caixas: [], cabos: [], regioes: [], postes: [], clientsCount: null, clientsStatus: 'idle' };

export const useOzmapData = ({ projectName, centerCoordinates, radius }) => {
    const [ozmapData, setOzmapData] = useState(EMPTY_DATA);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Depende dos valores primitivos das coordenadas: o objeto recebido é recriado
    // a cada atualização do formulário e dispararia buscas repetidas na API.
    const centerLat = Number(centerCoordinates?.lat);
    const centerLng = Number(centerCoordinates?.lng);

    useEffect(() => {
        const hasValidCoords = Number.isFinite(centerLat) && Number.isFinite(centerLng);

        if (!projectName || !hasValidCoords || !radius) {
            setOzmapData(EMPTY_DATA);
            // Sem isto, uma busca em andamento interrompida por coordenadas
            // inválidas deixaria o indicador de carregamento preso em true.
            setIsLoading(false);
            return undefined;
        }

        const centerCoords = { lat: centerLat, lng: centerLng };
        let isCurrent = true;

        const fetchSimpleData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const projectId = await ozmapService.getProjectByName(projectName);

                // Busca os elementos essenciais do mapa.
                const [caixas, regioes, postes] = await Promise.all([
                    ozmapService.fetchBoxesByRadius(projectId, centerCoords, radius),
                    ozmapService.fetchRegionsByRadius(projectId, centerCoords, radius),
                    ozmapService.fetchPolesByRadius(projectId, centerCoords, radius),
                ]);

                // Normaliza caixas para garantir que lat/lng existam (necessário se vier de 'select' com apenas 'coords')
                const normalizedCaixas = (caixas || []).map(box => {
                    let { lat, lng } = box;
                    if (box.coords) {
                        if (Array.isArray(box.coords) && box.coords.length >= 2) {
                             // Ozmap via select retorna [lng, lat]
                            lng = box.coords[0];
                            lat = box.coords[1];
                        } else if (typeof box.coords === 'object') {
                            lat = box.coords.lat;
                            lng = box.coords.lng;
                        }
                    }
                    return { ...box, lat, lng };
                });

                // Extrai IDs de cabos das caixas encontradas
                const cableIds = new Set();
                normalizedCaixas.forEach(box => {
                    if (box.cables && Array.isArray(box.cables)) {
                        box.cables.forEach(cableId => cableIds.add(cableId));
                    }
                });

                // Busca os cabos pelos IDs coletados
                let cabos = [];
                if (cableIds.size > 0) {
                    cabos = await ozmapService.fetchCablesByIds(Array.from(cableIds));
                }

                // Formata as regiões para o formato do Leaflet
                const formattedRegioes = regioes.map(r => ({
                    ...r,
                    coordinates: r.polygon?.coordinates?.[0]?.map(c => [c[1], c[0]]) || []
                }));

                if (!isCurrent) return;

                // Publica o mapa imediatamente: a contagem de clientes exige uma
                // segunda rodada de requisições e não pode segurar a renderização.
                setOzmapData({
                    caixas: normalizedCaixas,
                    cabos,
                    regioes: formattedRegioes,
                    postes,
                    clientsCount: null,
                    clientsStatus: 'loading',
                });
                setIsLoading(false);

                const clientsCount = await ozmapService.fetchClientsCountByRadius(projectId, centerCoords, radius);

                if (!isCurrent) return;
                setOzmapData(prev => ({
                    ...prev,
                    clientsCount,
                    clientsStatus: clientsCount === null ? 'error' : 'ready',
                }));

            } catch (err) {
                if (!isCurrent) return;
                setError(err);
                console.error("Falha na busca de dados do Ozmap:", err.message);
            } finally {
                if (isCurrent) setIsLoading(false);
            }
        };

        fetchSimpleData();

        // Descarta respostas de uma busca anterior quando as coordenadas mudam.
        return () => { isCurrent = false; };
    }, [projectName, centerLat, centerLng, radius]);

    return { ozmapData, isLoading, error };
};
